import { Link } from "react-router-dom";
import { ChevronRight, Info } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { DataKeys } from "../../../types";
import { useLang } from "../../Context";
import { StaggerInView, StaggerInViewItem } from "../../tools/Animations";
import { HealthProductSuiteIcon } from "../HealthProductSuiteIcons";
import { HealthSection } from "../HealthSection";
import { HealthSectionHeader } from "../HealthSectionHeader";
import {
    AimmaProductSuiteGroupId,
    getLocalized,
    HealthLang,
    LocalizedString,
} from "../healthTypes";
import { healthAimmaDoctorSectionContentOffset } from "../healthLayout";
import {
    HEALTH_BG_GREY,
    healthEcosystemCardThemeForId,
    healthIntegrationEcosystemCardBody,
    healthIntegrationEcosystemCardBodyDisabled,
    healthIntegrationEcosystemCardCtaFor,
    healthIntegrationEcosystemCardDisabled,
    healthButtonArrowFa,
    healthIntegrationEcosystemCardGrid,
    healthIntegrationEcosystemCardHeader,
    healthIntegrationEcosystemCardLinkedFor,
    healthIntegrationEcosystemCardStatusDisabled,
    healthIntegrationEcosystemCardTitle,
    healthIntegrationEcosystemCardTitleDisabled,
    healthIntegrationEcosystemSectionStack,
    healthIntegrationEcosystemPreviewCardGrid,
    healthIntegrationPreviewHubGridItem,
    healthIntegrationPreviewHubGridItemInner,
    healthPhysicianControlNote,
    healthPhysicianControlNoteIcon,
    healthPhysicianControlNoteText,
    sortHealthEcosystemCardsById,
} from "../healthVisualStyles";
import { healthLocalePath } from "./healthLocalePath";
import { HealthIntegrationPreviewCard } from "../aimmaNurse/HealthIntegrationPreviewCard";

interface HealthIntegrationEcosystemSectionProps {
    data: DataKeys;
    surfaceColor?: string;
    cardLayout?: "default" | "preview";
}

const ICON_STROKE = 1.75;

function comingSoonLabel(lang: HealthLang): string {
    if (lang === "Zh") return "即將推出";
    if (lang === "Jp") return "近日公開";
    return "Coming soon";
}

interface EcosystemCardContentProps {
    cardId: string;
    groupId: AimmaProductSuiteGroupId;
    title: string;
    body: string;
    ctaLabel: string;
    comingSoon: boolean;
    hLang: HealthLang;
}

function EcosystemCardContent({
    cardId,
    groupId,
    title,
    body,
    ctaLabel,
    comingSoon,
    hLang,
}: EcosystemCardContentProps) {
    return (
        <>
            <div className={healthIntegrationEcosystemCardHeader}>
                <HealthProductSuiteIcon
                    size="group"
                    groupId={cardId === "hub" ? "doctor" : groupId}
                    theme={healthEcosystemCardThemeForId(cardId)}
                    muted={comingSoon}
                />
                {comingSoon && (
                    <span className={healthIntegrationEcosystemCardStatusDisabled}>
                        {comingSoonLabel(hLang)}
                    </span>
                )}
            </div>
            <h3
                className={`${healthIntegrationEcosystemCardTitle} ${
                    comingSoon ? healthIntegrationEcosystemCardTitleDisabled : ""
                }`}
            >
                {title}
            </h3>
            <p
                className={`${healthIntegrationEcosystemCardBody} ${
                    comingSoon ? healthIntegrationEcosystemCardBodyDisabled : ""
                }`}
            >
                {body}
            </p>
            {!comingSoon && (
                <span
                    className={healthIntegrationEcosystemCardCtaFor(
                        healthEcosystemCardThemeForId(cardId)
                    )}
                >
                    {ctaLabel}
                    <FontAwesomeIcon
                        icon={faArrowRight}
                        className={healthButtonArrowFa}
                        aria-hidden="true"
                    />
                </span>
            )}
        </>
    );
}

export function HealthIntegrationEcosystemSection({
    data,
    surfaceColor = HEALTH_BG_GREY,
    cardLayout = "default",
}: HealthIntegrationEcosystemSectionProps) {
    const { lang } = useLang();
    const hLang = lang as HealthLang;
    const cards = sortHealthEcosystemCardsById((data.cards as DataKeys[]) ?? []);
    const flowStrip = data.flowStrip as DataKeys | undefined;
    const flowSteps = (flowStrip?.steps as DataKeys[]) ?? [];

    return (
        <HealthSection
            id="integration"
            ariaLabelledBy="integration-title"
            compact
            style={{ backgroundColor: surfaceColor }}
        >
            <HealthSectionHeader
                eyebrow={getLocalized(data.eyebrow as LocalizedString, hLang)}
                title={getLocalized(data.title as LocalizedString, hLang)}
                titleId="integration-title"
                subtitle={getLocalized(data.intro as LocalizedString, hLang)}
                subtitleWide
            />

            <div className={healthAimmaDoctorSectionContentOffset}>
                <div className={healthIntegrationEcosystemSectionStack}>
                    {flowSteps.length > 0 ? (
                        <div
                            className="flex w-full min-w-0 flex-col gap-3 rounded-2xl border border-grey-100/80 bg-white px-4 py-4 shadow-[0_8px_24px_rgba(16,24,40,0.04)] lg:flex-row lg:flex-wrap lg:items-center lg:gap-2 lg:px-5 lg:py-5"
                            aria-label={
                                hLang === "Zh"
                                    ? "照護資料流"
                                    : hLang === "Jp"
                                      ? "ケアデータフロー"
                                      : "Care data flow"
                            }
                        >
                            {flowSteps.map((step, index) => {
                                const stepId = step.id as string;
                                const stepLabel = getLocalized(
                                    step.label as LocalizedString,
                                    hLang
                                );
                                const stepDetail = getLocalized(
                                    step.detail as LocalizedString,
                                    hLang
                                );

                                return (
                                    <div
                                        key={stepId}
                                        className="flex min-w-0 flex-1 items-center gap-2 lg:min-w-[9rem]"
                                    >
                                        {index > 0 ? (
                                            <ChevronRight
                                                className="hidden h-4 w-4 shrink-0 text-teal-500 lg:block"
                                                strokeWidth={2}
                                                aria-hidden="true"
                                            />
                                        ) : null}
                                        <div className="min-w-0 flex-1 text-left">
                                            <p className="text-sm font-semibold text-grey-800">
                                                {stepLabel}
                                            </p>
                                            <p className="text-xs leading-relaxed text-grey-500">
                                                {stepDetail}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : null}

                    <StaggerInView
                        className={
                            cardLayout === "preview"
                                ? healthIntegrationEcosystemPreviewCardGrid
                                : healthIntegrationEcosystemCardGrid
                        }
                    >
                        {cards.map((card) => {
                            const cardId = card.id as string;
                            const comingSoon = Boolean(card.comingSoon);
                            const groupId =
                                cardId === "patient" ||
                                cardId === "nurse" ||
                                cardId === "doctor"
                                    ? (cardId as AimmaProductSuiteGroupId)
                                    : "doctor";
                            const title = getLocalized(card.title as LocalizedString, hLang);
                            const body = getLocalized(card.body as LocalizedString, hLang);
                            const ctaLabel = getLocalized(
                                card.ctaLabel as LocalizedString,
                                hLang
                            );
                            const href = healthLocalePath(card.href as string, hLang);

                            if (cardLayout === "preview") {
                                const hubCentersOnRowTwo =
                                    cardId === "hub" && cards.length === 4;

                                return (
                                    <StaggerInViewItem
                                        key={cardId}
                                        direction="bottom"
                                        className={
                                            hubCentersOnRowTwo
                                                ? healthIntegrationPreviewHubGridItem
                                                : "h-full"
                                        }
                                    >
                                        <div
                                            className={
                                                hubCentersOnRowTwo
                                                    ? healthIntegrationPreviewHubGridItemInner
                                                    : "h-full w-full"
                                            }
                                        >
                                            <HealthIntegrationPreviewCard
                                                cardId={cardId}
                                                groupId={groupId}
                                                title={title}
                                                body={body}
                                                ctaLabel={ctaLabel}
                                                comingSoon={comingSoon}
                                                hLang={hLang}
                                                href={comingSoon ? undefined : href}
                                            />
                                        </div>
                                    </StaggerInViewItem>
                                );
                            }

                            const contentProps = {
                                cardId,
                                groupId,
                                title,
                                body,
                                ctaLabel,
                                comingSoon,
                                hLang,
                            };

                            if (comingSoon) {
                                return (
                                    <StaggerInViewItem key={cardId} direction="bottom">
                                        <article
                                            className={healthIntegrationEcosystemCardDisabled}
                                            aria-disabled="true"
                                        >
                                            <EcosystemCardContent {...contentProps} />
                                        </article>
                                    </StaggerInViewItem>
                                );
                            }

                            return (
                                <StaggerInViewItem key={cardId} direction="bottom">
                                    <Link
                                        to={href}
                                        className={healthIntegrationEcosystemCardLinkedFor(
                                            cardId
                                        )}
                                    >
                                        <EcosystemCardContent {...contentProps} />
                                    </Link>
                                </StaggerInViewItem>
                            );
                        })}
                    </StaggerInView>

                    <div className={healthPhysicianControlNote}>
                        <span
                            className={healthPhysicianControlNoteIcon}
                            aria-hidden="true"
                        >
                            <Info
                                className="h-4 w-4"
                                strokeWidth={ICON_STROKE}
                            />
                        </span>
                        <p className={healthPhysicianControlNoteText}>
                            {getLocalized(
                                data.deploymentNote as LocalizedString,
                                hLang
                            )}
                        </p>
                    </div>
                </div>
            </div>
        </HealthSection>
    );
}
